import { Parameter } from '@shared/types';
import i18n from '@/i18n/i18n';

/**
 * Japanese translation map for common parameter name words.
 * Maps English words (lowercase) to their Japanese equivalents.
 */
const jaParameterWords: Record<string, string> = {
  // Dimensions
  width: '幅',
  height: '高さ',
  depth: '奥行き',
  length: '長さ',
  thickness: '厚さ',
  radius: '半径',
  diameter: '直径',
  size: 'サイズ',
  distance: '距離',
  dimension: '寸法',

  // Positions & offsets
  offset: 'オフセット',
  margin: 'マージン',
  padding: 'パディング',
  gap: '隙間',
  spacing: '間隔',
  inset: 'インセット',
  position: '位置',
  center: '中心',

  // Directions & locations
  front: '前面',
  back: '背面',
  top: '上部',
  bottom: '底部',
  left: '左',
  right: '右',
  inner: '内側',
  outer: '外側',
  edge: 'エッジ',
  corner: 'コーナー',

  // Structure
  base: 'ベース',
  stand: 'スタンド',
  wall: '壁',
  lip: 'リップ',
  groove: '溝',
  slot: 'スロット',
  notch: 'ノッチ',
  tab: 'タブ',
  hole: '穴',
  layer: 'レイヤー',
  shell: 'シェル',
  support: 'サポート',

  // Shape operations
  fillet: 'フィレット',
  chamfer: '面取り',
  round: '丸み',
  extrude: '押出し',
  bevel: 'ベベル',

  // Counts & quantities
  count: '数',
  number: '数',
  rows: '列数',
  row: '行',
  columns: '列',
  column: '列',
  total: '合計',
  min: '最小',
  max: '最大',

  // Properties
  angle: '角度',
  rotation: '回転',
  scale: 'スケール',
  weight: '重さ',
  density: '密度',
  resolution: '解像度',
  detail: 'ディテール',
  color: '色',
  pattern: 'パターン',
  parameters: 'パラメータ',
  parameter: 'パラメータ',

  // Joinery & hardware
  screw: 'ネジ',
  bolt: 'ボルト',
  nut: 'ナット',
  dowel: 'ダウエル',
  tenon: 'ほぞ',
  mortise: 'ほぞ穴',
  hinge: '蝶番',
  shelf: '棚板',
  drawer: '引出し',
  pin: 'ピン',
  clearance: 'クリアランス',
  tolerance: '公差',
  pilot: '下穴',
  countersink: '皿穴',
  counterbore: '座ぐり',

  // Materials
  material: '材質',
  wood: '木材',
  plywood: '合板',
  mdf: 'MDF',
  steel: 'スチール',
  aluminum: 'アルミニウム',

  // Parts
  leg: '脚',
  rail: '桟',
  panel: 'パネル',
  frame: 'フレーム',
  door: 'ドア',
  side: '側面',
  seat: '座面',
  arm: 'アーム',
  apron: '幕板',
  stretcher: '貫',
  slat: 'スラット',
  board: '板',
  bracket: 'ブラケット',

  // General
  overall: '全体',
  main: 'メイン',
  sub: 'サブ',
  body: '本体',
  assembly: '組立',
  component: '部品',
  part: '部品',
  cross: '十字',
  type: 'タイプ',
  style: 'スタイル',
  enabled: '有効',
  amount: '量',
};

const jaParameterPhrases: Record<string, string> = {
  wall_thickness: '壁厚',
  shell_thickness: 'シェル厚',
  material_thickness: '材料厚',
  total_width: '全体幅',
  total_height: '全体高さ',
  total_depth: '全体奥行き',
  overall_width: '全体幅',
  overall_height: '全体高さ',
  overall_depth: '全体奥行き',
  hole_diameter: '穴径',
  pilot_diameter: '下穴径',
  clearance_diameter: 'クリアランス径',
  countersink_diameter: '皿穴径',
  countersink_depth: '皿穴深さ',
  counterbore_diameter: '座ぐり径',
  counterbore_depth: '座ぐり深さ',
  corner_radius: '角R',
  fillet_radius: 'フィレットR',
  chamfer_size: '面取り寸法',
  shelf_spacing: '棚間隔',
  slot_width: '溝幅',
  slot_depth: '溝深さ',
  panel_thickness: 'パネル厚',
  base_height: 'ベース高さ',
  base_width: 'ベース幅',
  base_depth: 'ベース奥行き',
};

function normalizeParameterLabel(label: string): string {
  return label
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_/()-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function humanizeParameterLabel(label: string): string {
  return label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Translates a parameter display name or description into the active locale.
 *
 * - In Japanese: replaces known English words/phrases with their Japanese
 *   equivalents using the dictionaries above. Strings that already contain
 *   CJK characters are returned untouched.
 * - In other locales (English / Arabic): the dictionary doesn't apply, so we
 *   return a humanized version of the input (e.g. `total_width` \u2192 `Total Width`).
 *   Strings that already contain CJK characters fall back to the same
 *   humanizing \u2014 better than leaking JP text into a non-JP UI.
 */
export function translateParameterLabel(label: string): string {
  if (!label) {
    return label;
  }

  const lang = i18n.language ?? '';
  const isJapanese = lang.startsWith('ja');
  const containsCjk = /[\u3000-\u9fff]/.test(label);

  if (isJapanese) {
    if (containsCjk) {
      return label;
    }
    const normalized = normalizeParameterLabel(label);
    const compactKey = normalized.toLowerCase().replace(/\s+/g, '_');
    if (jaParameterPhrases[compactKey]) {
      return jaParameterPhrases[compactKey];
    }
    const words = normalized.split(' ');
    const translated = words.map(
      (word) => jaParameterWords[word.toLowerCase()] ?? word,
    );
    return translated.join(' ');
  }

  // en / ar / others: avoid showing Japanese text. If the source already
  // happens to be CJK, humanize the underscores anyway as a graceful fallback.
  if (containsCjk) {
    return humanizeParameterLabel(label);
  }
  const normalized = normalizeParameterLabel(label);
  return humanizeParameterLabel(normalized);
}

/**
 * Calculates intuitive min and max values for a parameter based on its default value
 * Creates ranges like: 94mm -> 0-100, 2.7 -> 0-10, 0.5 -> 0-1
 * Range is fixed based on default value to prevent runaway expansion
 */
export function calculateParameterRange(param: Parameter): {
  min: number;
  max: number;
} {
  // If explicit range is provided, use it
  if (param.range?.min !== undefined && param.range?.max !== undefined) {
    return { min: param.range.min, max: param.range.max };
  }

  const defaultValue = Number(param.defaultValue);

  // Use only the default value for range calculation to prevent runaway expansion
  const referenceValue = Math.abs(defaultValue);

  // Handle zero or very small values
  if (referenceValue <= 0.001) {
    return { min: 0, max: 1 };
  }

  // Calculate the order of magnitude
  const magnitude = Math.floor(Math.log10(referenceValue));
  const normalizedValue = referenceValue / Math.pow(10, magnitude);

  let rangeMultiplier: number;

  // Determine appropriate range based on normalized value
  // Round up to next nice number: 1, 2, 5, or 10
  if (normalizedValue <= 1) {
    rangeMultiplier = 1;
  } else if (normalizedValue <= 2) {
    rangeMultiplier = 2;
  } else if (normalizedValue <= 5) {
    rangeMultiplier = 5;
  } else {
    rangeMultiplier = 10;
  }

  let maxValue = rangeMultiplier * Math.pow(10, magnitude);

  // Special handling for values that should round up to next nice range
  // e.g., 94 -> 100, 2.7 -> 10 (since 2.7 is closer to 10 than 5)
  if (referenceValue > maxValue * 0.5) {
    if (rangeMultiplier === 1) {
      rangeMultiplier = 2;
    } else if (rangeMultiplier === 2) {
      rangeMultiplier = 5;
    } else if (rangeMultiplier === 5) {
      rangeMultiplier = 10;
    } else {
      rangeMultiplier = 10;
      maxValue = Math.pow(10, magnitude + 1);
    }
    maxValue = rangeMultiplier * Math.pow(10, magnitude);
  }

  // Determine minimum value
  let minValue: number;
  if (param.range?.min !== undefined) {
    minValue = param.range.min;
  } else {
    // For negative default values, use symmetric range
    // For positive default values, start from 0
    if (defaultValue < 0) {
      minValue = -maxValue;
    } else {
      minValue = 0;
    }
  }

  return { min: minValue, max: maxValue };
}

/**
 * Calculates an appropriate step size for a parameter based on its range
 */
export function calculateParameterStep(param: Parameter): number {
  // If explicit step is provided, use it
  if (param.range?.step !== undefined) {
    return param.range.step;
  }

  const { min, max } = calculateParameterRange(param);
  const range = max - min;

  // Handle very small ranges
  if (range <= 0.001) {
    return 0.001;
  }

  // Calculate step as 1% of the range, rounded to a nice number
  const rawStep = range / 100;
  const magnitude = Math.floor(Math.log10(rawStep));
  const normalizedStep = rawStep / Math.pow(10, magnitude);

  let stepMultiplier: number;
  if (normalizedStep <= 1) {
    stepMultiplier = 1;
  } else if (normalizedStep <= 2) {
    stepMultiplier = 2;
  } else if (normalizedStep <= 5) {
    stepMultiplier = 5;
  } else {
    stepMultiplier = 10;
  }

  return stepMultiplier * Math.pow(10, magnitude);
}

/**
 * Determines if a parameter represents a measurement in millimeters
 */
export function isMeasurementParameter(param: Parameter): boolean {
  // Common measurement-related terms
  const measurementTerms = [
    'width',
    'height',
    'length',
    'depth',
    'thickness',
    'radius',
    'diameter',
    'distance',
    'size',
    'offset',
    'gap',
    'spacing',
    'margin',
    'padding',
    'inset',
    'extrude',
    'dimension',
  ];

  // Check if parameter name or displayName contains measurement terms
  const nameToCheck = param.name.toLowerCase();
  const displayNameToCheck = param.displayName.toLowerCase();

  // Only consider number type parameters
  if (param.type !== 'number' && param.type !== undefined) {
    return false;
  }

  // Check if the parameter name or display name contains any measurement terms
  return measurementTerms.some(
    (term) => nameToCheck.includes(term) || displayNameToCheck.includes(term),
  );
}

/**
 * Validates and sanitizes a parameter value
 */
export function validateParameterValue(
  param: Parameter,
  value: Parameter['value'],
): Parameter['value'] {
  if (param.type === 'number' || !param.type) {
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return Number(param.defaultValue);
    }
    return numValue;
  }

  return value;
}

export function updateParameter(code: string, param: Parameter): string {
  const escapedName = escapeRegExp(param.name);
  const regex = new RegExp(
    `^\\s*(${escapedName}\\s*=\\s*)[^;]+;([\\t\\f\\cK ]*\\/\\/[^\n]*)?`,
    'm',
  );
  // Default to assuming the type is number
  if (!param.type) {
    return code.replace(regex, `$1${param.value};$2`);
  }
  switch (param.type) {
    case 'string':
      return code.replace(
        regex,
        `$1"${escapeReplacement(escapeQuotes(param.value as string))}";$2`,
      );
    case 'number':
      return code.replace(regex, `$1${param.value};$2`);
    case 'boolean':
      return code.replace(regex, `$1${param.value};$2`);
    case 'string[]':
      return code.replace(
        regex,
        `$1[${(param.value as string[])
          .map((value) => escapeReplacement(escapeQuotes(value)))
          .map((value) => `"${value}"`)
          .join(',')}];$2`,
      );
    case 'number[]':
      return code.replace(
        regex,
        `$1[${(param.value as number[]).join(',')}];$2`,
      );
    case 'boolean[]':
      return code.replace(
        regex,
        `$1[${(param.value as boolean[]).join(',')}];$2`,
      );
    default:
      return code;
  }
}

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

function escapeReplacement(string: string) {
  return string.replace(/\$/g, '$$$$');
}

function escapeQuotes(string: string) {
  return string.replace(/"/g, '\\"');
}
